'use client'
import SEO from '../components/SEO/SEO'
import { useState, useEffect } from 'react'
import Layout from '../layout/Layout'
import { useRouter } from 'next/router'
import { BASE_URL } from '../constants'

const currentYear = new Date().getFullYear()

const FormsAndPolicies = () => {
  const [allForms, setAllForms] = useState([])
  const [currentFormList, setCurrentFormList] = useState([])
  const [year, setYear] = useState(currentYear)
  const [category, setCategory] = useState('Members')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const getYears = numYears => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: numYears }, (_, i) => currentYear - i)
  }
  const years = getYears(3)

  const [filters, setFilters] = useState([['Members', 'Providers'], years])

  const router = useRouter()
  const { type } = router.query

  useEffect(() => {
    if (!router.isReady) return

    fetchAllForms()

    // Set initial category from URL or default to Members
    if (type) {
      setCategory(type)
    } else {
      const defaultCategory = 'Members'
      setCategory(defaultCategory)

      // Update URL with default params without page reload
      router.push(
        {
          pathname: router.pathname,
          query: { type: defaultCategory },
        },
        undefined,
        { shallow: true }
      )
    }
  }, [router.isReady])

  // When type changes in URL, update category
  useEffect(() => {
    if (type) {
      setCategory(type)
    }
  }, [type])

  // Fetch all forms once
  const fetchAllForms = async () => {
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`${BASE_URL}/Marketing/GetFormFiles`, {
        method: 'POST',
        body: JSON.stringify({ page: 'Forms' }),
        headers: {
          'content-type': 'application/json',
        },
      })

      if (!res.ok) {
        throw new Error(res.statusText)
      }

      const data = await res.json()
      setAllForms(data)
      setLoading(false)
    } catch (err) {
      setError(err)
      setLoading(false)
    }
  }

  // Filter forms when category, year, or allForms changes
  useEffect(() => {
    filterForms()
  }, [category, year, allForms])

  // Client-side filtering function
  const filterForms = () => {
    if (!allForms || allForms.length === 0) return

    const filtered = allForms
      .filter(
        form =>
          form.categories.includes(category) &&
          form.years.includes(parseInt(year))
      )
      .sort((a, b) => a.name.localeCompare(b.name))

    setCurrentFormList(filtered)
  }

  const handleSearch = () => {
    filterForms()
  }

  return (
    <>
      <SEO />
      <Layout>
        <div className="relative">
          <div className="absolute inset-x-0 bottom-0 bg-gray-100 h-1/2" />
          <div className="mx-auto">
            <div className="relative">
              <div className="absolute inset-0">
                <img
                  className="object-cover w-full h-full"
                  src="filling-documents.png"
                  alt="Person filling out documents"
                />
              </div>
              <div className="relative max-w-screen-xl px-4 py-16 mx-auto md:py-16 lg:py-32">
                <h1 className="text-3xl tracking-tight md:text-5xl text-darkBlue">
                  Forms and Notices
                </h1>
              </div>
            </div>
          </div>
        </div>

        <section>
          <div className="relative max-w-screen-xl px-4 py-12 mx-auto sm:px-6 lg:px-8">
            <div className="flex flex-1 w-full max-w-screen-xl mx-auto">
              <div className="flex flex-col items-center justify-center w-full px-4 py-8 rounded-sm shadow-lg bg-white-light sm:px-6 lg:px-8">
                <div className="flex flex-col items-center justify-between w-full gap-14 md:flex-row">
                  <div className="flex flex-col w-full -mt-4 space-y-4 ">
                    <label
                      htmlFor="dates"
                      className="ml-4 text-sm font-semibold text-gray-700 font-small"
                    >
                      Dates
                    </label>
                    <select
                      id="dates"
                      name="dates"
                      onChange={e => setYear(e.target.value)}
                      value={year}
                      className="block w-full px-6 py-3 text-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-green focus:border-green sm:text-sm"
                    >
                      {filters[1].map((year, index) => (
                        <option key={index}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col w-full -mt-4 space-y-4">
                    <label
                      htmlFor="categories"
                      className="ml-4 text-sm font-semibold text-gray-700"
                    >
                      Categories
                    </label>
                    <select
                      id="categories"
                      name="categories"
                      onChange={e => setCategory(e.target.value)}
                      value={category}
                      className="block w-full px-6 py-3 text-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-green focus:border-green sm:text-sm"
                    >
                      {filters[0].map((category, index) => (
                        <option key={index}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    <button
                      type="submit"
                      className="inline-flex items-center px-6 py-3 text-lg font-medium text-white border border-transparent shadow-sm bg-green hover:bg-darkGreen focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green"
                      onClick={handleSearch}
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 lg:grid lg:grid-cols-2 lg:gap-8">
              <div className="mb-10 lg:col-span-1 md:mb-0">
                <h2 className="py-12 pb-0 text-4xl tracking-tight sm:text-3xl lg:text-4xl text-green">
                  Texas Forms - {category} - {year}
                </h2>
              </div>
              <div className="lg:col-span-1">
                <div className="flex items-center justify-center h-full">
                  <div className="flow-root min-w-full mt-6">
                    {currentFormList && currentFormList.length > 0 ? (
                      <>
                        <ul
                          role="list"
                          className="pb-4 divide-y divide-lightGrey md:w-full"
                        >
                          {currentFormList.map((file, index) => (
                            <li key={index} className="py-4">
                              <div className="flex items-center space-x-4">
                                <div className="flex-1 min-w-0">
                                  <p className="text-xl text-grey">
                                    {file.name}
                                  </p>
                                </div>
                                <div>
                                  <a
                                    href={`https://api.evryhealth.com/api/v1/Marketing/GetFileContent?id=${file.file_id}`}
                                    className="inline-flex items-center px-2.5 py-0.5"
                                  >
                                    <img
                                      className="h-8"
                                      src="download-icon.svg"
                                      alt="Download form"
                                    />
                                  </a>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      !loading &&
                      !error && (
                        <div className="text-center">
                          <p className="text-2xl md:text-4xl">
                            No forms found for the selected filters.
                          </p>
                        </div>
                      )
                    )}

                    {loading && (
                      <div className="text-center">
                        <p className="text-2xl md:text-4xl">Loading...</p>
                      </div>
                    )}

                    {error && (
                      <div className="text-center">
                        <p className="text-2xl md:text-4xl">
                          Something went wrong. Please try again later.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  )
}

export default FormsAndPolicies
